import {
  ChangeDetectorRef,
  Directive,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  inject,
} from '@angular/core';
import { MatTable } from '@angular/material/table';

/**
 * Alimenta el `dataSource` de una `mat-table` de a chunks, un chunk por frame,
 * hasta renderizar todas las filas.
 *
 * Con listados grandes (500+ filas) crear todas las filas de una es una tarea
 * larga que congela la UI (cada fila instancia celdas, iconos, botón y tooltip).
 * Repartirla en varios frames mantiene la interfaz respondiendo.
 *
 * No se pierde ninguna fila: siempre se termina renderizando el array completo.
 * El contador nunca baja, así que un refresco del listado no desmonta filas ya
 * renderizadas.
 *
 * Uso: reemplaza a `[dataSource]`
 * ```html
 * <mat-table [appFilasProgresivas]="filas" [trackBy]="trackPorId">
 * ```
 */
@Directive({
  selector: 'mat-table[appFilasProgresivas], table[appFilasProgresivas]',
  standalone: true,
})
export class FilasProgresivasDirective<T> implements OnChanges, OnDestroy {
  private table = inject<MatTable<T>>(MatTable);
  private zone = inject(NgZone);
  private cdr = inject(ChangeDetectorRef);

  @Input('appFilasProgresivas') datos: T[] = [];
  /** Filas por frame. 50 ya cubre más de una pantalla en el primer frame. */
  @Input() chunk = 50;

  private renderizadas = 0;
  private frame?: number;

  ngOnChanges(): void {
    this.aplicar();
  }

  private aplicar(): void {
    const datos = this.datos || [];
    const total = datos.length;

    if (this.renderizadas === 0) {
      this.renderizadas = Math.min(this.chunk, total);
    } else if (this.renderizadas > total) {
      this.renderizadas = total;
    }

    this.table.dataSource =
      this.renderizadas >= total ? datos : datos.slice(0, this.renderizadas);

    if (this.renderizadas < total) this.programarSiguienteChunk();
  }

  private programarSiguienteChunk(): void {
    if (this.frame !== undefined) return;
    this.zone.runOutsideAngular(() => {
      this.frame = requestAnimationFrame(() => {
        this.frame = undefined;
        this.zone.run(() => {
          this.renderizadas = Math.min(
            this.renderizadas + this.chunk,
            (this.datos || []).length,
          );
          this.aplicar();
          // El componente host es OnPush: sin esto las filas nuevas no se
          // chequean y quedan vacías.
          this.cdr.markForCheck();
        });
      });
    });
  }

  ngOnDestroy(): void {
    if (this.frame !== undefined) cancelAnimationFrame(this.frame);
  }
}
